import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { SUPPORT_FILE_ACCEPT, SUPPORT_MAX_FILES } from "../../utils/constants/support";
import { formatFileSize, isImageFile, supportFilesProblem } from "../../utils/helpers/supportFiles";

const CLIP_PATH =
  "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13";

/** Picked-but-not-sent files, with image previews and a remove button each. */
export function AttachmentChips({ files, onRemove, disabled = false }) {
  const { t } = useTranslation();
  const previews = useMemo(
    () => files.map((file) => (isImageFile(file) ? URL.createObjectURL(file) : null)),
    [files],
  );
  useEffect(
    () => () => previews.forEach((url) => url && URL.revokeObjectURL(url)),
    [previews],
  );
  if (!files.length) return null;

  return (
    <ul className="flex flex-wrap gap-2">
      {files.map((file, index) => (
        <li
          key={`${file.name}-${file.size}-${index}`}
          className="relative flex items-center gap-2 max-w-[220px] rounded-xl border border-[color:var(--support-border)] bg-[color:var(--support-chip)] pl-1.5 pr-8 py-1.5"
        >
          {previews[index] ? (
            <img src={previews[index]} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
          ) : (
            <span className="w-10 h-10 rounded-lg bg-[color:var(--support-surface)] flex items-center justify-center text-[10px] font-black uppercase text-[color:var(--support-muted)] shrink-0">
              {file.name.split(".").pop()}
            </span>
          )}
          <span className="min-w-0">
            <span className="block truncate text-xs font-semibold text-[color:var(--support-surface-text)]">
              {file.name}
            </span>
            <span className="block text-[11px] text-[color:var(--support-muted)]">
              {formatFileSize(file.size)}
            </span>
          </span>
          <button
            type="button"
            onClick={() => onRemove(index)}
            disabled={disabled}
            aria-label={t("support.removeFile", { name: file.name })}
            className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center text-[color:var(--support-muted)] hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * The "attach" button and its hidden file input. Validates the pick against
 * the server's limits and toasts the first problem instead of adding it.
 */
export function AttachButton({ files, onChange, disabled = false, compact = false }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const full = files.length >= SUPPORT_MAX_FILES;

  const handlePick = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = ""; // allow re-picking the same file after removing it
    if (!picked.length) return;
    const problem = supportFilesProblem(picked, files.length);
    if (problem) {
      toast.error(t(problem));
      return;
    }
    onChange([...files, ...picked]);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={SUPPORT_FILE_ACCEPT}
        onChange={handlePick}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || full}
        title={full ? t("support.errors.tooManyFiles") : t("support.attachHint")}
        aria-label={t("support.attach")}
        className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold text-[color:var(--support-muted)] hover:bg-black/5 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={CLIP_PATH} />
        </svg>
        {!compact && <span>{t("support.attach")}</span>}
      </button>
    </>
  );
}
